import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers } from '../../../mocks/step6Data';

interface TeamMember {
  id: string;
  name: string;
  phone: string;
  status: 'aktif' | 'pasif' | 'ilerliyor' | 'tamamladi';
  step1Completed: boolean;
  lastContact: string;
  note: string;
  joinDate: string;
  ownTeam?: TeamMember[];
  inviteToken?: string;
  memberUserId?: string;
}

interface SupabaseMember {
  id: string;
  user_id: string;
  name: string;
  phone: string;
  status: string;
  step1_completed: boolean;
  last_contact: string;
  note: string;
  join_date: string;
  parent_id: string | null;
  sort_order: number;
  invite_token: string | null;
  member_user_id: string | null;
}

const statusColors: Record<string, string> = { aktif: 'bg-green-500', pasif: 'bg-red-500', ilerliyor: 'bg-yellow-500', tamamladi: 'bg-blue-500' };
const statusLabels: Record<string, string> = { aktif: 'Aktif', pasif: 'Pasif', ilerliyor: 'İlerliyor', tamamladi: 'Tamamladı' };
const statusBg: Record<string, string> = { aktif: 'rgba(34,197,94,0.1)', pasif: 'rgba(239,68,68,0.1)', ilerliyor: 'rgba(234,179,8,0.1)', tamamladi: 'rgba(59,130,246,0.1)' };
const statusText: Record<string, string> = { aktif: 'text-green-400', pasif: 'text-red-400', ilerliyor: 'text-yellow-400', tamamladi: 'text-blue-400' };

function mapSupabaseToTeamMember(sm: SupabaseMember, children: SupabaseMember[]): TeamMember {
  return {
    id: sm.id,
    name: sm.name,
    phone: sm.phone,
    status: sm.status as TeamMember['status'],
    step1Completed: sm.step1_completed,
    lastContact: sm.last_contact,
    note: sm.note,
    joinDate: sm.join_date,
    inviteToken: sm.invite_token || undefined,
    memberUserId: sm.member_user_id || undefined,
    ownTeam: children.map(c => ({
      id: c.id,
      name: c.name,
      phone: c.phone,
      status: c.status as TeamMember['status'],
      step1Completed: c.step1_completed,
      lastContact: c.last_contact,
      note: c.note,
      joinDate: c.join_date,
      inviteToken: c.invite_token || undefined,
      memberUserId: c.member_user_id || undefined,
    })),
  };
}

interface AddPersonForm { name: string; phone: string; note: string; }

interface MemberCardProps {
  member: TeamMember;
  onSelect: (m: TeamMember) => void;
}

function MemberCard({ member, onSelect }: MemberCardProps) {
  const isRegistered = !!member.memberUserId;

  return (
    <div className="rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:translate-y-[-2px] group"
      style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}
      onClick={() => onSelect(member)}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
            {member.name.charAt(0)}
          </div>
          <div>
            <div className="text-sm font-semibold text-white">{member.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">{member.phone}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: statusBg[member.status] }}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusColors[member.status]}`} />
            <span className={statusText[member.status]}>{statusLabels[member.status]}</span>
          </div>
        </div>
      </div>

      {/* Invite Status */}
      {member.inviteToken && (
        <div className="rounded-lg p-2 mb-3 flex items-center gap-2"
          style={{ background: isRegistered ? 'rgba(34,197,94,0.08)' : 'rgba(245,166,35,0.08)' }}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`${isRegistered ? 'ri-user-follow-line text-green-400' : 'ri-user-shared-line text-amber-400'} text-sm`} />
          </div>
          <span className={`text-xs font-medium ${isRegistered ? 'text-green-400' : 'text-amber-400'}`}>
            {isRegistered ? 'Kayıt oldu' : 'Davet bekliyor'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs text-gray-500 mb-1">1. Adım</div>
          {member.step1Completed
            ? <div className="flex items-center gap-1 text-xs text-blue-400 font-medium"><i className="ri-checkbox-circle-fill" />Tamamlandı</div>
            : <div className="flex items-center gap-1 text-xs text-red-400 font-medium"><i className="ri-close-circle-line" />Eksik</div>}
        </div>
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <div className="text-xs text-gray-500 mb-1">Son İletişim</div>
          <div className="text-xs text-gray-300 font-medium">{member.lastContact}</div>
        </div>
      </div>

      {member.note && (
        <div className="rounded-lg p-2 mb-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <p className="text-xs text-gray-400 italic">&ldquo;{member.note}&rdquo;</p>
        </div>
      )}

      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
        <a href={`https://wa.me/${member.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
          <i className="ri-whatsapp-line text-sm" />
          Mesaj
        </a>
        <button className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-gray-300 cursor-pointer transition-all hover:bg-white/10 whitespace-nowrap"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
          onClick={() => onSelect(member)}>
          <i className="ri-eye-line text-sm" />
          Detay
        </button>
      </div>
    </div>
  );
}

interface DetailPanelProps {
  member: TeamMember;
  onClose: () => void;
  isLider: boolean;
  onNoteSave: (id: string, note: string) => void;
  subTeam: TeamMember[];
  subTeamLoading: boolean;
}

function DetailPanel({ member, onClose, isLider, onNoteSave, subTeam, subTeamLoading }: DetailPanelProps) {
  const [note, setNote] = useState(member.note);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const inviteUrl = member.inviteToken
    ? `${window.location.origin}${__BASE_PATH__}/auth/register?invite=${member.inviteToken}`
    : '';

  const handleCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const input = document.createElement('input');
      input.value = inviteUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onNoteSave(member.id, note);
    setSaving(false);
  };

  const isRegistered = !!member.memberUserId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
              {member.name.charAt(0)}
            </div>
            <div>
              <div className="text-base font-bold text-white flex items-center gap-2">
                {member.name}
                {isRegistered && <i className="ri-verified-badge-fill text-green-400 text-sm" title="Kayıt oldu" />}
              </div>
              <div className="text-xs text-gray-500">{member.phone}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[32rem] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className={`text-sm font-bold ${statusText[member.status]}`}>{statusLabels[member.status]}</div>
              <div className="text-xs text-gray-600 mt-1">Durum</div>
            </div>
            <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className={`text-sm font-bold ${member.step1Completed ? 'text-blue-400' : 'text-red-400'}`}>{member.step1Completed ? 'Tamamlandı' : 'Eksik'}</div>
              <div className="text-xs text-gray-600 mt-1">1. Adım</div>
            </div>
            <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="text-sm font-bold text-gray-300">{member.lastContact}</div>
              <div className="text-xs text-gray-600 mt-1">Son İletişim</div>
            </div>
          </div>

          {/* Invite Link Section */}
          {member.inviteToken && (
            <div className="rounded-xl p-4" style={{ background: isRegistered ? 'rgba(34,197,94,0.05)' : 'rgba(245,166,35,0.05)', border: `1px solid ${isRegistered ? 'rgba(34,197,94,0.15)' : 'rgba(245,166,35,0.15)'}` }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: isRegistered ? 'rgba(34,197,94,0.12)' : 'rgba(245,166,35,0.12)' }}>
                  <i className={`${isRegistered ? 'ri-user-follow-line text-green-400' : 'ri-user-shared-line text-amber-400'} text-sm`} />
                </div>
                <span className={`text-xs font-bold ${isRegistered ? 'text-green-400' : 'text-amber-400'}`}>
                  {isRegistered ? 'Kayıt Tamamlandı' : 'Davet Linki'}
                </span>
              </div>
              {isRegistered ? (
                <p className="text-xs text-gray-400">{member.name} kaydını tamamladı ve kendi paneline erişebiliyor.</p>
              ) : (
                <>
                  <p className="text-xs text-gray-400 mb-3">Bu linki {member.name} ile paylaş, kayıt olunca otomatik olarak ekibine bağlansın.</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 rounded-lg px-3 py-2 text-xs text-gray-300 truncate" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {inviteUrl}
                    </div>
                    <button onClick={handleCopy}
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:bg-white/10 whitespace-nowrap"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <i className={`${copied ? 'ri-check-line text-green-400' : 'ri-file-copy-line text-gray-400'} text-sm`} />
                    </button>
                    <a href={`https://wa.me/${member.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Merhaba ${member.name}! STEP6 Network ekibime katılmanı istiyorum. Bu linkten kayıt olabilirsin:\n\n${inviteUrl}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
                      style={{ background: '#25D366' }}>
                      <i className="ri-whatsapp-line text-white text-sm" />
                    </a>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sub-team section */}
          {isLider && (() => {
            const isRegistered = !!member.memberUserId;
            const ownTeamCount = member.ownTeam?.length || 0;
            const fetchedCount = subTeam.length;
            const totalSubCount = ownTeamCount + fetchedCount;

            if (totalSubCount === 0 && !subTeamLoading) return null;

            return (
              <div>
                <h3 className="text-sm font-bold text-white mb-3">
                  {member.name.split(' ')[0]}&rsquo;ın Ekibi
                  {totalSubCount > 0 && <span className="text-gray-500 font-normal ml-1">({totalSubCount} kişi)</span>}
                </h3>

                {/* Fetched sub-team from Supabase (member's own İlk 4) */}
                {isRegistered && (
                  <>
                    {subTeamLoading ? (
                      <div className="flex items-center gap-2 py-3">
                        <div className="w-4 h-4 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
                        <span className="text-xs text-gray-500">{member.name.split(' ')[0]}&rsquo;ın ekibi yükleniyor...</span>
                      </div>
                    ) : fetchedCount > 0 ? (
                      <div className="space-y-2 mb-3">
                        {subTeam.map(tm => (
                          <div key={tm.id} className="flex items-center gap-3 p-3 rounded-xl"
                            style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.1)' }}>
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #34C759 0%, #30B350 100%)' }}>
                              {tm.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-white truncate">{tm.name}</div>
                              <div className="text-xs text-gray-500">{tm.phone}</div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${statusColors[tm.status]}`} />
                              {tm.step1Completed && <i className="ri-checkbox-circle-fill text-blue-400 text-xs" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-600 mb-3">{member.name.split(' ')[0]} henüz kendi İlk 4&rsquo;ünü eklememiş.</p>
                    )}
                  </>
                )}

                {/* L2 members from leader's own data */}
                {ownTeamCount > 0 && (
                  <div className="space-y-2">
                    {member.ownTeam!.map(tm => (
                      <div key={tm.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #6B7280 0%, #374151 100%)' }}>
                          {tm.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white truncate">{tm.name}</div>
                          <div className="text-xs text-gray-500">{tm.phone}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${statusColors[tm.status]}`} />
                          {tm.step1Completed && <i className="ri-checkbox-circle-fill text-blue-400 text-xs" />}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <div>
            <label className="text-xs font-medium text-gray-400 mb-2 block">Not Ekle / Güncelle</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder="Bu kişiyle ilgili notlarını buraya ekle..."
              className="w-full rounded-xl px-3 py-2.5 text-sm text-white resize-none outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }} />
          </div>

          <div className="flex gap-3 flex-wrap">
            <a href={`https://wa.me/${member.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
              <i className="ri-whatsapp-line" />
              WhatsApp Aç
            </a>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap disabled:opacity-50"
              style={{ background: 'rgba(245,166,35,0.15)', border: '1px solid rgba(245,166,35,0.2)' }}>
              <i className={`${saving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} ${saving ? 'text-gray-400' : 'text-amber-400'}`} />
              {saving ? 'Kaydediliyor...' : 'Notu Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Ilk4KisimProps {
  userRole: string;
}

export default function Ilk4Kisim({ userRole }: Ilk4KisimProps) {
  const { profile } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<AddPersonForm>({ name: '', phone: '', note: '' });
  const [saving, setSaving] = useState(false);

  // Sub-team fetching for registered members
  const [subTeam, setSubTeam] = useState<TeamMember[]>([]);
  const [subTeamLoading, setSubTeamLoading] = useState(false);

  // Fetch sub-team when a registered member is selected
  useEffect(() => {
    if (!selectedMember || !selectedMember.memberUserId) {
      setSubTeam([]);
      return;
    }

    let cancelled = false;
    setSubTeamLoading(true);

    supabase
      .from('step6_team_members')
      .select('*')
      .eq('user_id', selectedMember.memberUserId)
      .order('sort_order', { ascending: true })
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError || !data) {
          setSubTeam([]);
        } else {
          const allSub = data as SupabaseMember[];
          const subL1 = allSub.filter(m => !m.parent_id);
          const subL2 = allSub.filter(m => m.parent_id);
          const mapped = subL1.map(l1 =>
            mapSupabaseToTeamMember(l1, subL2.filter(l2 => l2.parent_id === l1.id))
          );
          setSubTeam(mapped);
        }
        setSubTeamLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedMember]);

  useEffect(() => {
    if (!profile) {
      setMembers(mockTeamMembers);
      setLoading(false);
      return;
    }

    let cancelled = false;

    supabase
      .from('step6_team_members')
      .select('*')
      .eq('user_id', profile.id)
      .order('sort_order', { ascending: true })
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError || !data || data.length === 0) {
          setMembers(mockTeamMembers);
          setError('');
        } else {
          const allMembers = data as SupabaseMember[];
          const l1Members = allMembers.filter(m => !m.parent_id);
          const l2Members = allMembers.filter(m => m.parent_id);

          const mapped: TeamMember[] = l1Members.map(l1 =>
            mapSupabaseToTeamMember(l1, l2Members.filter(l2 => l2.parent_id === l1.id))
          );
          setMembers(mapped);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [profile]);

  const handleAdd = async () => {
    if (!addForm.name || !addForm.phone) return;
    if (!profile) return;
    if (members.length >= 4) return;

    setSaving(true);

    const inviteToken = crypto.randomUUID();

    const { data, error: insertError } = await supabase
      .from('step6_team_members')
      .insert({
        user_id: profile.id,
        name: addForm.name,
        phone: addForm.phone,
        note: addForm.note,
        status: 'pasif',
        step1_completed: false,
        last_contact: new Date().toISOString().split('T')[0],
        join_date: new Date().toISOString().split('T')[0],
        sort_order: members.length,
        invite_token: inviteToken,
      })
      .select()
      .single();

    if (insertError || !data) {
      setSaving(false);
      return;
    }

    const newMember: TeamMember = {
      id: data.id,
      name: data.name,
      phone: data.phone,
      status: data.status as TeamMember['status'],
      step1Completed: data.step1_completed,
      lastContact: data.last_contact,
      note: data.note,
      joinDate: data.join_date,
      inviteToken: data.invite_token || undefined,
      memberUserId: data.member_user_id || undefined,
      ownTeam: [],
    };

    setMembers(prev => [...prev, newMember]);
    setSaving(false);
    setShowAddForm(false);
    setAddForm({ name: '', phone: '', note: '' });

    // Log action
    await supabase.from('step6_action_logs').insert({
      user_id: profile.id,
      person_name: addForm.name,
      action: 'Yeni kişi eklendi + davet linki oluşturuldu',
      type: 'add',
    });
  };

  const handleNoteSave = async (id: string, note: string) => {
    if (!profile) return;

    const { error: updateError } = await supabase
      .from('step6_team_members')
      .update({ note })
      .eq('id', id)
      .eq('user_id', profile.id);

    if (updateError) return;

    // Update local state
    setMembers(prev => prev.map(m => {
      if (m.id === id) return { ...m, note };
      if (m.ownTeam) {
        const updatedTeam = m.ownTeam.map(t => t.id === id ? { ...t, note } : t);
        return { ...m, ownTeam: updatedTeam };
      }
      return m;
    }));

    if (selectedMember && selectedMember.id === id) {
      setSelectedMember(prev => prev ? { ...prev, note } : null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400">Ekip bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  const step1Count = members.filter(m => m.step1Completed).length;
  const activeCount = members.filter(m => m.status === 'aktif' || m.status === 'tamamladi').length;

  return (
    <div className="space-y-6">
      {selectedMember && (
        <DetailPanel
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
          isLider={userRole === 'lider' || userRole === 'admin'}
          onNoteSave={handleNoteSave}
          subTeam={subTeam}
          subTeamLoading={subTeamLoading}
        />
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">İlk 4 Kişim</h1>
          <p className="text-sm text-gray-500 mt-1">Ekibinin çekirdeği — 4 kişini yönet ve büyüt</p>
        </div>
        <button onClick={() => setShowAddForm(true)} disabled={members.length >= 4}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
          <i className="ri-user-add-line text-base" />
          Yeni Kişi Ekle
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: `${members.length}/4`, color: 'text-white' },
          { label: 'Aktif', value: activeCount, color: 'text-green-400' },
          { label: '1. Adım', value: step1Count, color: 'text-blue-400' },
          { label: 'Pasif', value: members.filter(m => m.status === 'pasif').length, color: 'text-red-400' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <p className="text-red-400">{error}</p>
          <button onClick={() => { setError(''); setMembers(mockTeamMembers); }} className="text-amber-400 hover:text-amber-300 font-medium mt-1">
            Mock veriyle devam et →
          </button>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {members.map(member => (
          <MemberCard key={member.id} member={member} onSelect={setSelectedMember} />
        ))}
        {members.length < 4 && (
          <button onClick={() => setShowAddForm(true)}
            className="rounded-2xl p-5 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all hover:border-amber-500/30 min-h-52 whitespace-nowrap"
            style={{ background: 'rgba(245,166,35,0.03)', border: '2px dashed rgba(245,166,35,0.15)' }}>
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.1)' }}>
              <i className="ri-user-add-line text-xl text-amber-400" />
            </div>
            <span className="text-sm text-amber-400 font-medium">Kişi Ekle</span>
            <span className="text-xs text-gray-600 text-center">{4 - members.length} boşluk kaldı</span>
          </button>
        )}
      </div>

      {/* Durum Renk Rehberi */}
      <div className="rounded-2xl p-4" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs font-medium text-gray-500 mb-3">Durum Renk Rehberi</p>
        <div className="flex flex-wrap gap-4">
          {Object.entries(statusLabels).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${statusColors[key]}`} />
              <span className="text-xs text-gray-400">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Add Person Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h2 className="text-base font-bold text-white">Yeni Kişi Ekle</h2>
              <button onClick={() => setShowAddForm(false)} className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
                <i className="ri-close-line text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Ad Soyad *</label>
                <input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })}
                  className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none text-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  placeholder="Örn: Ali Veli" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Telefon *</label>
                <input value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })}
                  className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none text-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  placeholder="+90 5XX XXX XX XX" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Not (isteğe bağlı)</label>
                <textarea value={addForm.note} onChange={e => setAddForm({ ...addForm, note: e.target.value })} rows={2}
                  className="w-full rounded-xl px-3 py-2.5 text-sm text-white resize-none outline-none text-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  placeholder="Bu kişiyle ilgili kısa not..." />
              </div>
              <button onClick={handleAdd} disabled={saving}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    Ekleniyor...
                  </span>
                ) : 'Kişiyi Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}